import {CountryService} from '@/Services';
import '@/Factories/lazyControllerFactory';
import {AdminUserRole, RolesType} from "@/models";
import {IAppConfig} from "@commons/constants";

type RouteConfigModel = {
    link: string;
    templateName?: string;
    templateUrl?: string;
    controller: string;
    controllerPath?: string;
    controllerAs?: string;
    data?: {
        roles?: RolesType[];
        allowAnonymous?: boolean;
    };
    params?: any;
}

export const registerRoutes = (app: IModule) => {
    app.config([
        '$stateProvider',
        '$locationProvider',
        '$urlRouterProvider',
        'APP_CONFIG',
        function ($stateProvider, $locationProvider, $urlRouterProvider, appConfig: IAppConfig) {

            /**
             * Map file name
             * */
            function getControllerFileName(controllerName) {
                if (!controllerName) {
                    return '';
                }

                return controllerName
                    .replace('_controller', '')
                    .replace('_ctrl', '')
                    .split('_')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join('');
            }

            function getTemplateFilePath(filePath, controllerName?: string) {
                if (!filePath) {
                    return
                }

                if (!controllerName) {
                    return `/${filePath}.html?v=${appConfig.version}`;
                }

                const folderName = getControllerFileName(controllerName);
                return `pages/${folderName}/${filePath}.html?v=${appConfig.version}`;
            }

            // Pages
            const links: RouteConfigModel[] = [
                    {
                        link: '/',
                        templateName: 'Home',
                        controller: 'main_ctrl'
                    },
                    {
                        link: '/Account/Login',
                        templateName: 'Login',
                        controller: 'auth_ctrl',
                        data: {
                            allowAnonymous: true
                        }
                    },
                    {
                        link: '/signin-:provider?code&state',
                        controller: 'auth_callback_ctrl',
                        params: {
                            code: null,
                            state: null
                        },
                        data: {
                            allowAnonymous: true
                        }
                    },
                    {
                        link: '/instoreorder',
                        templateName: 'ListInstoreOrder',
                        controller: "order_instore_ctrl",
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.EcommerceAdmin,
                                AdminUserRole.Cashier
                            ]
                        }
                    },
                    {
                        link: '/manage-user',
                        templateName: 'ManageAdministrators',
                        controller: 'admin_ctrl',
                        data: {
                            roles: [AdminUserRole.SuperAdmin]
                        },

                    },
                    {
                        link: '/storeview',
                        templateName: 'ManageStoreView',
                        controller: 'storeview_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.EcommerceAdmin
                            ]
                        },

                    },
                    {
                        link: '/customer-groups',
                        templateName: 'ManageCustomerGroup',
                        controller: 'customer_group_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.CustomerServiceAdmin,
                                AdminUserRole.Ecommerce,
                                AdminUserRole.EcommerceAdmin,
                                AdminUserRole.Dealer
                            ]
                        },

                    },
                    {
                        link: '/categories',
                        templateName: 'ManageCategories',
                        controller: 'category_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.Ecommerce,
                                AdminUserRole.EcommerceAdmin
                            ]
                        }
                    },
                    {
                        link: '/statistics/popular-products',
                        templateUrl: 'pages/ManageStatistics/PopularProducts/ManageStatisticsPopularProducts',
                        controller: 'manage_statistics_popular_products_ctrl',
                        controllerPath: 'pages/ManageStatistics/PopularProducts/ManageStatisticsPopularProductsController'
                    },
                    {
                        link: '/statistics/best-seller-products',
                        templateUrl: 'pages/ManageStatistics/BestSellerProducts/ManageStatisticsBestSellerProducts',
                        controller: 'manage_statistics_best_seller_products_ctrl',
                        controllerPath: 'pages/ManageStatistics/BestSellerProducts/ManageStatisticsBestSellerProductsController'
                    },
                    {
                        link: '/master-data-list',
                        templateName: 'MasterDataList',
                        controller: 'master_data_list_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.Ecommerce,
                                AdminUserRole.EcommerceAdmin
                            ]
                        }
                    },
                    {
                        link: '/master-data-list/editing/:dataListId',
                        templateName: 'EditMasterDataList',
                        controller: 'edit_master_data_list_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.Ecommerce,
                                AdminUserRole.EcommerceAdmin
                            ]
                        }
                    },
                    {
                        link: '/products',
                        templateName: 'ManageProducts',
                        controller: 'manage_products_ctrl',
                        data: {
                            roles: [
                                "Super Admin", "Ecommerce", "Ecommerce Admin", "Dealer"
                            ]
                        }
                    },
                    {
                        link: '/products/editing/:sku?countryId&distributor&tab',
                        templateName: 'EditingProduct',
                        params: {
                            countryId: {value: null},
                            distributor: {dynamic: true, value: null},
                            tab: {dynamic: true, value: null}
                        },
                        controller: 'edit_product_ctrl',
                        data: {
                            roles: ["Super Admin", "Ecommerce", "Ecommerce Admin"]
                        },

                    },
                    {
                        link: '/customers',
                        templateName: 'ManageCustomers',
                        controller: 'customer_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.CustomerService,
                                AdminUserRole.CustomerServiceAdmin,
                                AdminUserRole.EcommerceAdmin,
                                AdminUserRole.Dealer
                            ]
                        },
                    },
                    {
                        link: '/customers/editing/:customerId',
                        templateName: 'EditingCustomer',
                        controller: 'edit_customer_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.CustomerService,
                                AdminUserRole.CustomerServiceAdmin,
                                AdminUserRole.EcommerceAdmin,
                                AdminUserRole.Dealer
                            ]
                        },

                    },
                    {
                        link: '/purchasers',
                        templateName: 'ManagePurchaser',
                        controller: 'manage_purchaser_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.CustomerServiceAdmin,
                                AdminUserRole.EcommerceAdmin,
                                AdminUserRole.Dealer
                            ]
                        },
                    },
                    {
                        link: '/dealers',
                        templateName: 'DistributorManagement',
                        controller: 'distributor_management_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.EcommerceAdmin,
                                AdminUserRole.CustomerServiceAdmin,
                                AdminUserRole.CustomerService,
                                AdminUserRole.Ecommerce
                            ]
                        }
                    },
                    {
                        link: '/dealer-groups',
                        templateName: 'ManageDealerGroups',
                        controller: 'distributor_group_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.EcommerceAdmin,
                                AdminUserRole.CustomerServiceAdmin,
                                AdminUserRole.CustomerService
                            ]
                        },

                    },
                    {
                        link: '/dealers/editing/:numberDealer',
                        templateName: 'EditDistributor',
                        controller: 'edit_distributor_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.EcommerceAdmin,
                                AdminUserRole.CustomerServiceAdmin,
                                AdminUserRole.CustomerService,
                                AdminUserRole.Ecommerce
                            ]
                        },

                    },
                    {
                        link: '/dealer/profile',
                        templateName: 'EditDistributor',
                        controller: 'edit_distributor_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.Dealer
                            ]
                        }
                    },
                    {
                        link: '/home-frontend',
                        templateName: 'HomeFrontEnd',
                        controller: 'frontend_home_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.EcommerceAdmin
                            ]
                        }
                    },
                    {
                        link: '/imagestore',
                        templateName: 'ImageStorage',
                        controller: 'image_storage_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.CustomerServiceAdmin,
                                AdminUserRole.EcommerceAdmin
                            ]
                        },

                    },
                    {
                        link: '/product-promotions',
                        templateName: 'DataListPromotions',
                        controller: 'data_list_promotion_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.Ecommerce,
                                AdminUserRole.EcommerceAdmin
                            ]
                        }
                    },
                    {
                        link: '/localization',
                        templateName: 'Localization',
                        controller: 'localization_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.EcommerceAdmin
                            ]
                        },

                    },
                    {
                        link: '/orders',
                        templateName: 'Orders',
                        controller: 'orders_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.Dealer,
                                AdminUserRole.CustomerService,
                                AdminUserRole.CustomerServiceAdmin,
                                AdminUserRole.EcommerceAdmin
                            ]
                        },

                    },
                    {
                        link: '/orders/statistic',
                        templateUrl: 'pages/Orders/Statistic/OrderStatistic',
                        controller: 'order_statistic_ctrl',
                        controllerPath: 'pages/Orders/Statistic/OrderStatisticController',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.Dealer,
                                AdminUserRole.CustomerService,
                                AdminUserRole.CustomerServiceAdmin,
                                AdminUserRole.EcommerceAdmin
                            ]
                        },
                    },
                    {
                        link: '/order-status',
                        templateName: 'OrderStatusEmail',
                        controller: 'orders_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.Dealer,
                                AdminUserRole.CustomerService,
                                AdminUserRole.CustomerServiceAdmin,
                                AdminUserRole.SalesAdmin,
                                AdminUserRole.EcommerceTester,
                                AdminUserRole.Cashier,
                                AdminUserRole.EcommerceAdmin
                            ]
                        },

                    },
                    {
                        link: '/orders/:orderNumber/:tab',
                        params: {
                            tab: {
                                dynamic: true,
                                value: 'summary'
                            }
                        },
                        templateName: 'OrderDetail',
                        controller: 'order_detail_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.Dealer,
                                AdminUserRole.CustomerService,
                                AdminUserRole.CustomerServiceAdmin,
                                AdminUserRole.EcommerceAdmin
                            ]
                        }
                    },
                    {
                        link: '/synchronize-url',
                        templateName: 'UrlRewrite',
                        controller: 'url_rewrite_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.EcommerceAdmin,
                                AdminUserRole.CustomerServiceAdmin
                            ]
                        },

                    },
                    {
                        link: '/url-redirecting',
                        templateName: 'UrlRedirectingManage',
                        controller: 'url_redirect_controller',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.EcommerceAdmin
                            ]
                        },

                    },
                    {
                        link: '/tags',
                        templateName: 'Tags',
                        controller: 'tag_ctrl',
                        data: {
                            roles: [AdminUserRole.SuperAdmin, AdminUserRole.EcommerceAdmin]
                        }
                    },
                    {
                        link: '/filter',
                        templateName: 'SettingFilter',
                        controller: 'setting_filter_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.EcommerceAdmin
                            ]
                        }
                    },
                    {
                        link: '/newsletter-subscribers',
                        templateName: 'NewsletterSubscribers',
                        controller: 'newsletter_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.Ecommerce,
                                AdminUserRole.EcommerceAdmin
                            ]
                        }


                    },
                    {
                        link: '/email-templates',
                        templateName: 'ManageEmailTemplates',
                        controller: 'email_template_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.EcommerceAdmin
                            ]
                        }
                    },
                    {
                        link: '/returnreasons',
                        templateName: 'ReturnReasons',
                        controller: 'return_reason_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.EcommerceAdmin
                            ]
                        }
                    },
                    {
                        link: '/return-orders',
                        templateName: 'ReturnProducts',
                        controller: 'return_product_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.Dealer,
                                AdminUserRole.CustomerService,
                                AdminUserRole.CustomerServiceAdmin,
                                AdminUserRole.SalesAdmin,
                                AdminUserRole.EcommerceTester,
                                AdminUserRole.Cashier,
                                AdminUserRole.EcommerceAdmin
                            ]
                        }
                    },
                    {
                        link: '/export-orders',
                        templateName: 'ExportOrders',
                        controller: 'export_order_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.Dealer,
                                AdminUserRole.CustomerService,
                                AdminUserRole.CustomerServiceAdmin,
                                AdminUserRole.SalesAdmin,
                                AdminUserRole.EcommerceTester,
                                AdminUserRole.EcommerceAdmin
                            ]
                        }
                    },
                    {
                        link: '/other-pages',
                        templateName: 'OtherPages',
                        controller: 'other_page_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.EcommerceAdmin
                            ]
                        }
                    },
                    {
                        link: '/rating-emails',
                        templateName: 'RatingEmails',
                        controller: 'rating_email_ctrl',
                        data: {
                            roles: [AdminUserRole.SuperAdmin]
                        }
                    },
                    {
                        link: '/clearcache',
                        templateName: 'ClearCacheManage',
                        controller: 'clear_cache_ctrl',
                        data: {
                            roles: [AdminUserRole.SuperAdmin]
                        }
                    },
                    {
                        link: '/systemSetting',
                        templateName: 'SystemSettingManage',
                        controller: 'setting_ctrl',
                        data: {
                            roles: [AdminUserRole.SuperAdmin]
                        }
                    },
                    {
                        link: '/pageFAQ',
                        templateName: 'ManageFAQPage',
                        controller: 'FAQ_page_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.CustomerServiceAdmin,
                                AdminUserRole.EcommerceAdmin
                            ]
                        }
                    },
                    {
                        link: '/shipping-management',
                        templateName: 'ManageShipping',
                        controller: 'manage_shipping_ctrl',
                        data: {
                            roles: [AdminUserRole.All]
                        }
                    },
                    {
                        link: '/pricelist',
                        templateName: 'ManagePriceList',
                        controller: 'manage_pricelist_ctrl',
                        data: {
                            roles: [AdminUserRole.All]
                        }
                    },
                    {
                        link: '/customer-group-discount',
                        templateName: 'ManageCustomerGroupDiscount',
                        controller: 'manage_customer_group_discount_ctrl',
                        data: {
                            roles: [AdminUserRole.All]
                        }
                    },
                    {
                        link: '/campaign',
                        templateName: 'ManageCampaign',
                        controller: 'manage_campaign_ctrl',
                        data: {
                            roles: [AdminUserRole.All]
                        }
                    },
                    {
                        link: '/coupon',
                        templateName: 'ManageCoupon',
                        controller: 'manage_coupon_ctrl',
                        data: {
                            roles: [AdminUserRole.All]
                        }
                    },
                    {
                        link: '/seo-generate',
                        templateName: 'SEODescriptionManage',
                        controller: 'seo_description_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.CustomerServiceAdmin,
                                AdminUserRole.EcommerceAdmin
                            ]
                        }
                    },
                    {
                        link: '/logHistory',
                        templateName: 'LogHistory',
                        controller: 'log_history_ctrl',
                        data: {
                            roles: [AdminUserRole.SuperAdmin]
                        }
                    },
                    {
                        link: '/last-visitors',
                        templateName: 'LastVisitors',
                        controller: 'last_visitors_ctrl',
                        data: {
                            roles: [
                                AdminUserRole.SuperAdmin,
                                AdminUserRole.CustomerServiceAdmin,
                                AdminUserRole.EcommerceAdmin
                            ] // Menu cha cho phép nhóm 3 vai trò cấu hình hệ thống
                        }
                    },
                    {
                        link: '/jobs-management',
                        templateName: 'JobsManagement',
                        controller: 'job_management_ctrl',
                        data: {
                            roles: [AdminUserRole.SuperAdmin]
                        }
                    },
                    {
                        link: '/profile',
                        templateName: 'Profile',
                        controller: 'profile_ctrl'
                    }
                ]
            ;

            // Init layout config
            $stateProvider.state('main_layout', {
                abstract: true,
                templateUrl: '/Layouts/MainLayout.html?v=' + appConfig.version,
                resolve: {
                    initialData: [
                        'CountryService',
                        function (countryService: CountryService) {
                            // get store-view data as initial data for main layout, so it can be shared to all child pages and directive;
                            return countryService.GetAllCountries();
                        }
                    ]
                }
            });

            links.forEach((item: RouteConfigModel) => {
                let stateName = item.link
                    .replaceAll('/', '_')
                    .replace(/^_/, '')
                    .replaceAll(':', '')
                    .toLowerCase();

                if (!stateName || item.link === '/') {
                    stateName = 'home';
                }

                // Child page in main layout with menu
                if (
                    !(item.controller == 'auth_ctrl' ||
                        item.controller == 'order_instore_ctrl' ||
                        item.controller == 'auth_callback_ctrl')
                ) {
                    stateName = 'main_layout.' + stateName;
                }

                // Cập nhật templateUrl nếu có controller
                if (item.controller && item.templateName) {
                    item.templateUrl = getTemplateFilePath(item.templateName, item.controller);
                } else if (item.templateUrl) {
                    item.templateUrl = getTemplateFilePath(item.templateUrl);
                }

                try {
                    $stateProvider.state(stateName, {
                        url: item.link,
                        templateUrl: item.templateUrl,
                        controller: item.controller,
                        controllerAs: item.controllerAs || '$ctrl',
                        data: item.data || {},
                        params: item.params,
                        resolve: {
                            loadController: [
                                'LazyControllerFactory',
                                function (lazyControllerFactory) {
                                    if (!item.controller && !item.controllerPath) {
                                        return null;
                                    }

                                    return lazyControllerFactory.load(item.controller, item.controllerPath);
                                }
                            ]
                        }
                    });
                } catch (error) {
                    console.error('Error registering route:', item.link, error);
                }
            });

            // Fallback: route not found
            $urlRouterProvider.otherwise('/');
            $locationProvider.html5Mode(true);
        }
    ]);

    return app;
};
